import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { QuestionScreen } from "../../screens/practice/QuestionScreen";
import { TheoryQuestionScreen, type TheoryMark } from "../../screens/practice/TheoryQuestionScreen";
import { YearPickScreen } from "../../screens/practice/YearPickScreen";
import { TopicPickScreen } from "../../screens/practice/TopicPickScreen";
import type { PaperType } from "../../components/PaperToggle";
import type { ExamChoice } from "../../lib/catalog";
import { ScreenState } from "../../components/ScreenState";
import { LeaveSessionSheet } from "../../components/LeaveSessionSheet";
import { useAsync } from "../../lib/useAsync";
import {
  replayOfflineSession,
  startPractice,
  submitPractice,
  type ApiQuestion,
  type TrialState,
} from "../../api/practice";
import { ApiError, currentUserId } from "../../api/client";
import { offlineSupported, pending } from "../../lib/offlineStore";
import { useUpgradeOnPaywall } from "../../lib/upgrade";
import { markTheory } from "../../api/tutor";
import { fetchTopics, fetchYears } from "../../api/catalog";
import { packSession, packTopics, packYears } from "../../lib/offlinePacks";
import { cached, isNetworkFailure } from "../../lib/offlineCache";
import { OfflineNote } from "../../components/OfflineNote";
import { activeExam, useActiveExam } from "../../lib/activeExam";
import { reportMissingTopic } from "../../api/feedback";
import { BackButton } from "../../components/BackButton";
import { Button } from "../../components/Button";

/**
 * The practice flow, wired end to end.
 *
 * These were placeholders with `onPick={() => {}}`, so a student could reach
 * the year picker and tap a year to no effect — the screen looked finished and
 * was a dead end. Selection now carries the subject, paper and source into the
 * session, so what was picked is visible on the question header rather than
 * being silently discarded.
 */

interface PracticeSelection {
  subject?: string;
  subjectId?: string;
  topicIds?: string[];
  examYear?: number;
  paper?: PaperType;
  source?: string;
  exam?: ExamChoice;
  /** id → name, populated when entering via TopicPickRoute. */
}

function useSelection(): PracticeSelection {
  return (useLocation().state ?? {}) as PracticeSelection;
}

export function YearPickRoute() {
  const navigate = useNavigate();
  const selection = useSelection();
  const { subject = "", subjectId = "" } = selection;
  // The profile being shown decides the paper toggle: JAMB has no theory.
  const exam = useActiveExam() ?? selection.exam ?? "JAMB";
  const { data, loading, error, reload } = useAsync(
    () =>
      offlineFirst(
        `years.${subjectId}`,
        () => fetchYears(subjectId),
        () => packYears(subjectId),
      ),
    [subjectId, exam],
  );

  return (
    <ScreenState loading={loading} error={error} onRetry={reload}>
      <OfflineNote since={data?.savedAt ?? null} />
      {data ? (
        <YearPickScreen
          subject={subject}
          exam={exam}
          // Real years from the question bank, with real counts. The list used
          // to be four invented years, so picking "2023" for a subject with no
          // 2023 questions produced an empty session.
          years={data.data.years.map((year) => ({
            year: year.year,
            questionCount: year.questionCount,
          }))}
          onPick={(year, paper) => {
            const source = year === "mixed" ? "All years" : String(year);
            void navigate(paper === "theory" ? "/practice/theory" : "/practice/session", {
              state: {
                subject,
                subjectId,
                paper,
                source,
                exam,
                ...(year === "mixed" ? {} : { examYear: year }),
              },
            });
          }}
        />
      ) : null}
    </ScreenState>
  );
}

export function TopicPickRoute() {
  const navigate = useNavigate();
  const selection = useSelection();
  const { subject = "", subjectId = "" } = selection;
  // The profile being shown decides the paper toggle: JAMB has no theory.
  const exam = useActiveExam() ?? selection.exam ?? "JAMB";
  const { data, loading, error, reload } = useAsync(
    () =>
      offlineFirst(
        `topics.${subjectId}`,
        () => fetchTopics(subjectId),
        () => packTopics(subjectId),
      ),
    [subjectId, exam],
  );

  return (
    <ScreenState loading={loading} error={error} onRetry={reload}>
      <OfflineNote since={data?.savedAt ?? null} />
      {data ? (
        <TopicPickScreen
          subject={subject}
          exam={exam}
          topics={data.data.topics.map((topic) => ({
            id: topic.id,
            name: topic.name,
            mastery: topic.mastery,
            weak: topic.weak,
          }))}
          onPick={(topicId, paper) => {
            const picked = data.data.topics.find((topic) => topic.id === topicId);
            void navigate(paper === "theory" ? "/practice/theory" : "/practice/session", {
              state: {
                subject,
                subjectId,
                paper,
                source: picked?.name ?? "Topic practice",
                exam,
                topicIds: [topicId],
              },
            });
          }}
        />
      ) : null}
    </ScreenState>
  );
}

/** The API returns options as a map or a list depending on the question. */
function toOptions(options: ApiQuestion["options"]): { id: string; text: string }[] {
  if (Array.isArray(options)) return options;
  if (!options) return [];
  return Object.entries(options).map(([id, text]) => ({ id, text: String(text) }));
}

/**
 * A picker's list: from the server, saved for next time; offline, the saved
 * copy; with neither, worked out from the downloaded pack. Only a network
 * failure falls back — a real server error still shows as one.
 */
async function offlineFirst<T>(
  name: string,
  load: () => Promise<T>,
  fromPack: () => Promise<T | null>,
): Promise<{ data: T; savedAt: number | null }> {
  try {
    return await cached(name, load);
  } catch (error) {
    if (!isNetworkFailure(error)) throw error;
    const local = await fromPack();
    if (!local) throw error;
    return { data: local, savedAt: Date.now() };
  }
}

const OFFLINE_SESSION_SIZE = 10;

/** `crypto.randomUUID` is absent on http origins in some Android browsers. */
function newKey(): string {
  const random = globalThis.crypto;
  if (random && typeof random.randomUUID === "function") return `offline-${random.randomUUID()}`;
  return `offline-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

interface OpenSession {
  /** Null when the sitting came from a downloaded pack — nothing was opened. */
  sessionId: string | null;
  /** Sent on every attempt, so a queue drained twice scores once. */
  key: string;
  total: number;
  questions: ApiQuestion[];
  /** Free questions left, from the server. Null when the student has paid. */
  trial: TrialState | null;
  /** Readiness before this session, so the summary can show a real delta. */
  readinessBefore: number | null;
  /** True when these questions came off the device rather than the network. */
  fromPack: boolean;
}

/**
 * Open a session, falling back to a downloaded pack when there is no network.
 *
 * The fallback is deliberately narrow: only a network failure, and only when a
 * pack for that subject is actually on the device. A server that refuses the
 * request still surfaces its refusal — silently substituting local questions
 * for an entitlement error would let an unpaid student practise.
 */
async function openSession(
  subjectId: string,
  options: { topicIds?: string[] | undefined; examYear?: number | undefined },
): Promise<OpenSession> {
  try {
    const started = await startPractice({
      subjectId,
      mode: options.topicIds?.length ? "by_topic" : "by_year",
      ...(options.topicIds?.length ? { topicIds: options.topicIds } : {}),
      ...(options.examYear !== undefined ? { examYear: options.examYear } : {}),
    });

    return {
      sessionId: started.sessionId,
      key: started.sessionId,
      total: started.total,
      questions: started.questions,
      trial: started.trial ?? null,
      readinessBefore: started.readinessBefore ?? null,
      fromPack: false,
    };
  } catch (error) {
    if (!isNetworkFailure(error) || !subjectId || !offlineSupported()) throw error;

    // What they picked — a year, a topic — not just any ten from the subject.
    const drawn = await packSession<ApiQuestion>(subjectId, options, OFFLINE_SESSION_SIZE);
    if (!drawn) throw error;

    return {
      sessionId: null,
      key: newKey(),
      total: drawn.length,
      questions: drawn,
      // No server to ask while offline; the counter catches up on replay.
      trial: null,
      readinessBefore: null,
      fromPack: true,
    };
  }
}

/**
 * Group answers by topic and count them.
 *
 * Keyed on the name the question carries, not on an id looked up in a map the
 * caller was expected to pass. That map only existed when a student arrived
 * through the topic picker, so anyone who picked a *year* got a summary
 * listing raw uuids under "By topic".
 *
 * A question with no topic name is left out rather than shown under its id: a
 * row a student cannot read is worse than one fewer row.
 */
function buildTopicResults(
  questions: ApiQuestion[],
  recorded: { questionId: string; answer: string | null }[],
): { topic: string; correct: number; total: number; weak: boolean }[] {
  const byTopic = new Map<string, { correct: number; total: number }>();

  for (const entry of recorded) {
    const question = questions.find((q) => q.id === entry.questionId);
    if (!question) continue;

    const name = question.topicName?.trim();
    if (!name) continue;

    const row = byTopic.get(name) ?? { correct: 0, total: 0 };
    row.total += 1;
    if (question.correctAnswer != null && question.correctAnswer === entry.answer) row.correct += 1;
    byTopic.set(name, row);
  }

  return [...byTopic.entries()].map(([topic, { correct, total }]) => ({
    topic,
    correct,
    total,
    weak: total > 0 && correct / total < 0.5,
  }));
}

/**
 * A real session: opened against the API, answered locally, submitted as one
 * batch.
 *
 * This screen used to render `DEMO_QUESTION` — the same Chemistry electrolysis
 * question whatever the student picked, with the answer never leaving the
 * browser. Nothing reached the tracking engine, so readiness could not move.
 *
 * With no network it runs from a downloaded pack and queues the answers. What
 * a student does on a bus is the case this product exists for, so losing it is
 * the one failure that is not acceptable.
 */

/** True when the server said "No questions available" rather than a real crash. */
function isNoQuestionsError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 400 && /no questions/i.test(error.message);
}

/**
 * Empty topic: tell the student, let them tell us.
 *
 * The textarea message, the topic id and subject go to support in one email so
 * the team can prioritise which topics to populate next.
 */
function MissingTopicView({
  topicName,
  topicId,
  subjectName,
  subjectId,
  onBack,
}: {
  topicName: string;
  topicId: string;
  subjectName: string;
  subjectId: string;
  onBack: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await reportMissingTopic({
        topicId,
        topicName,
        subjectId,
        subjectName,
        message: draft.trim(),
      });
      setSent(true);
    } catch {
      // Failing to send the report must not strand the student — they still
      // need to get back to practice.
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/practice" />

      <div className="flex flex-1 flex-col pt-3">
        <h1 className="m-0 font-[family-name:var(--font-display)] text-[20px] font-bold">
          No questions yet
        </h1>
        <p className="m-0 pt-2 text-[13.5px] leading-[1.5] text-text-muted">
          We don't have questions for <span className="font-semibold text-text">{topicName}</span>{" "}
          in <span className="font-semibold text-text">{subjectName}</span> yet. Let us know what
          you need and we'll prioritise it.
        </p>

        {sent ? (
          <div className="mt-5 rounded-[var(--radius-card)] border border-positive bg-surface p-4">
            <p className="m-0 text-[13.5px] font-semibold">Thanks — we've got your message.</p>
            <p className="m-0 pt-1 text-[12.5px] leading-[1.5] text-text-muted">
              We'll work on adding questions for this topic.
            </p>
          </div>
        ) : (
          <div className="mt-5 flex flex-col gap-3">
            <label htmlFor="missing-topic-msg" className="text-[13px] font-semibold">
              Your message (optional)
            </label>
            <textarea
              id="missing-topic-msg"
              rows={4}
              maxLength={500}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="E.g. I really need this for my exam next month…"
              className="w-full resize-y rounded-[var(--radius-control)] border border-border-strong bg-surface p-3 text-[14px] text-text outline-none placeholder:text-text-muted focus:border-accent"
            />
            <Button disabled={sending} onClick={() => void send()}>
              {sending ? "Sending…" : "Send & go back"}
            </Button>
          </div>
        )}

        <div className="pt-4">
          <Button variant="ghost" onClick={onBack}>
            Back to topics
          </Button>
        </div>
      </div>
    </div>
  );
}

export function QuestionRoute() {
  const navigate = useNavigate();
  const { subject, subjectId, source, topicIds, examYear } = useSelection();
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [answers, setAnswers] = useState<
    { questionId: string; answer: string | null; timeTakenSeconds?: number }[]
  >([]);

  const state = useAsync(
    () => openSession(subjectId ?? "", { topicIds, examYear }),
    [subjectId, topicIds?.join(","), examYear],
  );

  useUpgradeOnPaywall(state.error);

  const started = state.data;
  const question = started?.questions[index];

  /**
   * Send the sitting, and keep it if the network is gone.
   *
   * The local count is the student's own marking against the answer the
   * question shipped with — the same marking the screen already showed them.
   * The server marks again on replay, and its result is what reaches mastery.
   */
  const finish = (
    session: OpenSession,
    recorded: { questionId: string; answer: string | null }[],
  ) => {
    const attempt = session.sessionId
      ? submitPractice(session.sessionId, recorded, session.key)
      : replayOfflineSession(subjectId ?? "", recorded, session.key);

    void attempt
      .then((result) =>
        navigate("/summary", {
          state: {
            subject,
            subjectId,
            // The real score, from the server that marked it.
            correct: result.totalCorrectAnswers,
            total: result.totalQuestions,
            // Taken when the session started — by now the engine has moved it.
            readinessBefore: session.readinessBefore,
            // Per-topic breakdown for the summary screen.
            topics: buildTopicResults(session.questions, recorded),
          },
        }),
      )
      .catch(async (error: unknown) => {
        if (!isNetworkFailure(error) || !offlineSupported()) {
          void navigate("/summary", { state: { subject, subjectId } });
          return;
        }

        const localCorrect = recorded.filter((entry) => {
          const asked = session.questions.find((item) => item.id === entry.questionId);
          return asked?.correctAnswer != null && asked.correctAnswer === entry.answer;
        }).length;

        try {
          await pending.add({
            id: session.key,
            sessionId: session.sessionId,
            subjectId: subjectId ?? null,
            answers: recorded,
            queuedAt: Date.now(),
            ownerId: currentUserId(),
            exam: activeExam(),
          });
        } catch {
          // Storage refused. Nothing left to do but tell them plainly.
          void navigate("/summary", { state: { subject, subjectId } });
          return;
        }

        void navigate("/summary", {
          state: {
            subject,
            subjectId,
            correct: localCorrect,
            total: recorded.length,
            queued: true,
          },
        });
      });
  };

  return (
    <ScreenState
      loading={state.loading}
      error={state.error}
      onRetry={state.reload}
      onBack={() => void navigate("/practice")}
      renderError={
        isNoQuestionsError(state.error) && topicIds?.[0]
          ? () => (
              <MissingTopicView
                topicId={topicIds?.[0] ?? ""}
                topicName={source ?? "this topic"}
                subjectId={subjectId ?? ""}
                subjectName={subject ?? ""}
                onBack={() =>
                  void navigate("/practice/topics", {
                    state: { subject, subjectId },
                  })
                }
              />
            )
          : undefined
      }
    >
      {started && question ? (
        <QuestionScreen
          question={{
            id: question.id,
            subject: subject ?? "Practice",
            source: source ?? String(question.examYear ?? "Practice"),
            index: index + 1,
            total: started.total,
            stem: question.stem,
            options: toOptions(question.options),
            // Shipped with the question so feedback works offline — see the
            // note on the practice endpoint.
            correctOptionId: question.correctAnswer ?? "",
            explanation: question.explanation ?? "",
          }}
          mode={started.fromPack ? "offline" : "past-question"}
          {...(started.trial?.shouldWarn ? { trialRemaining: started.trial.remaining } : {})}
          onNext={(_wasCorrect, chosenOptionId) => {
            const recorded = [
              ...answers,
              { questionId: question.id, answer: chosenOptionId ?? null },
            ];
            setAnswers(recorded);

            if (index + 1 < started.questions.length) {
              setIndex(index + 1);
              return;
            }

            finish(started, recorded);
          }}
          onClose={() => setLeaving(true)}
          onAskTutor={() =>
            void navigate("/tutor", {
              state: {
                questionId: question.id,
                subject,
                source: source ?? String(question.examYear ?? ""),
                stem: question.stem,
                correctAnswer: question.correctAnswer,
              },
            })
          }
        />
      ) : null}

      {started && leaving ? (
        <LeaveSessionSheet
          answeredCount={answers.length}
          remainingCount={started.questions.length - answers.length}
          onStay={() => setLeaving(false)}
          onLeave={() => {
            setLeaving(false);
            /*
             * Leaving sends what was answered rather than discarding it. A
             * student who stops after six of ten has still done six questions
             * of real work, and that work has to reach the mastery model or
             * the readiness number is a lie about how much they practised.
             */
            if (answers.length > 0) {
              finish(started, answers);
              return;
            }
            void navigate("/practice");
          }}
        />
      ) : null}
    </ScreenState>
  );
}

/**
 * WAEC theory, on real essay questions — and really marked.
 *
 * It was one hardcoded biology question about arteries and veins whatever
 * subject the student picked. The bank holds 6,211 real essay questions.
 *
 * The answer is now marked by the examiner-trained model against that
 * subject's WAEC scheme. The mark is feedback and nothing more: ADR-0006 keeps
 * AI marks out of the mastery model until an eval gate compares them against a
 * human-marked golden set, and the screen says so rather than letting a
 * student assume their readiness moved.
 */
export function TheoryRoute() {
  const navigate = useNavigate();
  const { subject = "", subjectId = "", source } = useSelection();
  const [index, setIndex] = useState(0);
  const [marking, setMarking] = useState(false);
  const [marked, setMarked] = useState<TheoryMark | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);

  const state = useAsync(
    () => startPractice({ subjectId, paper: "theory", count: 6 }),
    [subjectId],
  );

  useUpgradeOnPaywall(state.error);

  const started = state.data;
  const question = started?.questions[index];

  const mark = async (answer: string) => {
    if (!question || marking) return;
    setMarking(true);
    setMarkError(null);

    try {
      const result = await markTheory({ questionId: question.id, answer });
      setMarked(result);
    } catch (error) {
      // A marker that cannot answer must not read as practice failing — the
      // guide below still works, and the copy says which is which.
      setMarkError(
        error instanceof ApiError
          ? error.message
          : "We couldn't mark that. Compare your answer against the guide below.",
      );
    } finally {
      setMarking(false);
    }
  };

  return (
    <ScreenState loading={state.loading} error={state.error} onRetry={state.reload}>
      {started && question ? (
        <TheoryQuestionScreen
          question={{
            id: question.id,
            subject,
            source: source ? `${source} · Theory` : "Theory",
            index: index + 1,
            total: started.total,
            stem: question.stem,
            marks: question.marks ?? 0,
            // The examiner's model answer is the marking guide the student
            // reads their own answer against.
            guidance: question.marks ? `${question.marks} marks` : "Mark against the guide",
            points: (question.correctAnswer ?? "")
              .split(/\n|;/)
              .map((point) => point.trim())
              .filter(Boolean),
          }}
          {...(started.theoryMarkable && navigator.onLine
            ? { onMark: (answer: string) => void mark(answer) }
            : {})}
          marking={marking}
          marked={marked}
          markError={markError}
          offline={!navigator.onLine}
          onClose={() => void navigate("/practice")}
          onNext={() => {
            // Each question is marked on its own, so the last one's score must
            // not sit above the next one's blank page.
            setMarked(null);
            setMarkError(null);

            if (index + 1 < started.questions.length) {
              setIndex(index + 1);
              return;
            }
            void navigate("/summary", { state: { subject, subjectId } });
          }}
          onAskTutor={() =>
            void navigate("/tutor", {
              state: { questionId: question.id, subject, source, stem: question.stem },
            })
          }
        />
      ) : null}
    </ScreenState>
  );
}
